/**
 * Pick a music file and make it the timeline's audio track. The file is
 * cached locally before any upload, so it is playable immediately.
 */

import { useRef, useState } from 'react';
import { STORAGE_RULES } from '@canvas/firebase-config/config';
import { uploadProjectAudio } from '@/lib/video/videoAssetUpload';
import { useProjectStore } from '@/stores/projectStore';
import { useVideoStore } from '@/stores/videoStore';

interface AudioImportButtonProps {
  label?: string;
  className?: string;
}

export default function AudioImportButton({ label = '+ Add music', className = 'vt-btn' }: AudioImportButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const { setAudio, setNotice } = useVideoStore.getState();
    const projectId = useProjectStore.getState().currentProject?.projectId ?? '';
    setBusy(true);
    try {
      const track = await uploadProjectAudio({ file, projectId });
      setAudio(track);
      if (track.source === 'idb') {
        setNotice('Music added on this device only — it will back up once you are signed in and online.');
      }
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Could not add that audio file.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={className}
        disabled={busy}
        title="Add a music track (MP3, M4A, WAV, OGG · up to 20 MB)"
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Adding…' : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={[...STORAGE_RULES.ALLOWED_AUDIO_TYPES, '.mp3', '.m4a', '.wav', '.ogg'].join(',')}
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </>
  );
}
